/* The sequence canvas — `Builder planu sekwencja.dc.html`'s `v.grid` and its three kinds of column,
 * transcribed:
 *
 *   flex:1; min-height:0; display:flex; align-items:stretch; gap:10px;
 *   padding:14px 20px 16px; overflow-x:auto
 *
 * It is the week programator's row with different columns in it, which is the whole point of the
 * 07.09 mock: two canvases of one screen that had read as two different products now speak one
 * language. `WeekPane.module.css` is the twin and the two are deliberately parallel.
 *
 * ── `--text-3` IS used for text here ─────────────────────────────────────────────────────────
 * The 04.08 stylesheet this replaces opened with a refusal of KANON's tertiary ink on contrast
 * grounds, carrying the specimen's three-step hierarchy as a two-step one. The owner rejected that
 * result beside the week's mock on 8 September 2026 and the ruling governs here: the mock's inks
 * ship. A column head is `--text-2`, its duration line `--text-3`. The shortfall is real (2.80:1 on
 * a white card, 4.02:1 on a night one) and is the designer's to answer; nothing is told apart by it.
 *
 * ── The edge fades and the frame that held them are GONE ─────────────────────────────────────
 * The 04.08 canvas wrapped the scroller in a non-scrolling `.canvas` whose only job was to hold a
 * gradient over each clipped edge. The mock draws neither, and nothing the mock does not draw may be
 * visible (owner, 8 September 2026). The scroller is the pane directly. The SENTENCE the fades
 * carried survives on `aria-describedby` — see the component. */

/* `position: relative` is an ORDINARY style again, and the note it used to carry is recorded rather
 * than deleted.
 *
 * Every visually hidden box in this row was once `position: absolute` from a LOCAL class. With no
 * positioned ancestor their containing block is the initial one — the DOCUMENT — so each was laid
 * out at its static position in document coordinates, which on a fourteen-column row is thousands of
 * pixels to the right of the viewport: `documentElement` reported `scrollWidth: 4066` against a
 * 1512px viewport and the ENTIRE shell slid sideways. This declaration was what held that shut.
 *
 * It is no longer load-bearing: every one of those spans now uses `shared/ui/visuallyHidden`, whose
 * `position: fixed` takes the box out of the document's overflow rather than out of its flow alone,
 * and nothing in this row is absolutely positioned any more. The declaration stays as an ordinary
 * style — a scrollport with no positioning context is a trap for the next absolutely positioned
 * child somebody adds, and this note is the measurement that proves it. */
.pane {
  position: relative;
  display: flex;
  flex: 1;
  align-items: stretch;
  min-height: 0;

  /* Horizontal only. A column is as tall as the row, so nothing is ever hidden above or below it,
   * and letting the row scroll vertically would let a keyboard user push the whole canvas off. */
  overflow: auto hidden;
}

/* Inset, because the pane clips at its own edges and an outward ring would be cut off on both. */
.pane:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--outline-inset);
}

/* The mock's own row: `gap: 10px` and `padding: 14px 20px 16px`.
 *
 * `flex: 1 0 auto`, and the no-shrink half is load-bearing: with `flex: 1` the row shrinks to the
 * scroller and every card loses its bottom, so the load module is clipped away by the card's own
 * `overflow: hidden` with nothing to say so. The week's row carries the same pair for the same
 * measured reason. */
.cycle {
  /* The containing block for the trailing room below, and for that reason only. */
  position: relative;
  display: flex;
  flex: 1 0 auto;
  gap: var(--space-10px);
  align-items: stretch;
  min-width: 0;
  padding: var(--space-14px) var(--space-20px) var(--space-4);
}

/* How wide the room is: what covers the end, the day panel, the Buddy panel, or both side by side
 * (owner, 7 October 2026). */
.cycle[data-panel-room="day"] {
  --panel-room: var(--w-builder-panel);
}

.cycle[data-panel-room="assistant"] {
  --panel-room: var(--w-buddy-panel);
}

.cycle[data-panel-room="both"] {
  --panel-room: calc(var(--w-builder-panel) + var(--w-buddy-panel));
}

/* Trailing room the width of what covers the end, out of flow, so a column under it can be
 * scrolled clear of it — and so that not one column changes width when the panel opens.
 *
 * Without it `useRevealOpenDay` computes the right delta and the scroller has nowhere to go: on a
 * cycle whose columns already end before the panel's edge, `scrollLeft` runs to `scrollWidth` and
 * stops, leaving the opened day still covered. That is what shipped on 13 September and what the
 * owner reported.
 *
 * It sits on the ROW rather than on `.pane`, which is the week's own measured reason: Chrome does
 * not add a scroll container's end padding to `scrollWidth`, so the room measured 554px instead of
 * the panel's 574 and the covered column came 20px short. `1px` tall and pointer-transparent — it
 * exists to widen `scrollWidth` and nothing else. */
.cycle[data-panel-room]::after {
  position: absolute;
  top: 0;
  left: 100%;
  width: var(--panel-room);
  height: 1px;
  pointer-events: none;
  content: "";
}

/* ── One column, whichever kind ───────────────────────────────────────────────────────────────
 * `display:flex; flex-direction:column; gap:8px; min-height:0`, and a flex triple per kind.
 *
 * The one animated property this slice argues for: `CLAUDE.md` forbids animating a property that
 * lays the page out again, and `flex-basis` is exactly that. The mock declares
 * `transition: flex-grow .42s cubic-bezier(.2,.8,.3,1), flex-basis .42s …` on every column and the
 * widening IS the gesture — a gap going from zero to one day is a column growing from 84px to 108px
 * under the press that caused it. It can fire on nothing else, and it cannot fire on first paint,
 * because a transition animates a change from a previously rendered value and a freshly mounted
 * element has none. */
.column {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
  min-height: 0;
  transition:
    flex-grow var(--dur-grid-flex) var(--ease-fp),
    flex-basis var(--dur-grid-flex) var(--ease-fp);
}

/* A training: grows into whatever the gap columns leave over, from a 252px basis, capped at 496px so
 * a single training in a cycle does not become a document. `min-width` as well as the basis, because
 * the row may shrink and 252 is the floor the card's load module is readable at. */
.column[data-kind='day'] {
  flex: 1 1 var(--w-cycle-day);
  min-width: var(--w-cycle-day);
  max-width: var(--w-day-max);
}

/* A break: a fixed strip that neither grows nor shrinks — the room a gap does not need belongs to
 * the trainings. 84px at a gap of zero, where the tile prints "bez przerwy" instead of a figure. */
.column[data-kind='rest'] {
  flex: 0 0 var(--w-cycle-gap);
}

.column[data-kind='rest'][data-gap='zero'] {
  flex-basis: var(--w-cycle-gap-zero);
}

/* The two tail tiles, at the mock's one shared 120px basis. */
.column[data-kind='tail'] {
  flex: 0 0 var(--w-cycle-tail);
}

/* ── The column head ──────────────────────────────────────────────────────────────────────────
 * The mock's two centred lines 1px apart, `padding: 0 2px`, clipping rather than wrapping. It is
 * OUTSIDE the card — the head belongs to the cycle and the card is what moves.
 *
 * Neither span reserves a line. That is the mock's own layout: an empty `cap` or `sub` is zero
 * pixels tall, so a gap column's tile and both tail tiles start higher than the cards beside them,
 * and that step is visible in the mock's own render. Nothing moves as a result — a column's head
 * gains its lines together with the column. */
.head {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-1px);
  align-items: center;
  padding: var(--space-0) var(--space-2px);
  overflow: hidden;
}

/* "DZIEŃ 3" / "DNI 4–5". Uppercased HERE rather than in the locale: a shout-cased locale value is
 * read letter by letter by some screen readers, and the sentence case is also what a translator
 * needs to see. */
.cap {
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-rail);
  text-transform: uppercase;
  color: var(--text-2);
  white-space: nowrap;
}

/* A gap's head is a step quieter than a training's — the mock's own `--text-3` on `s.capSt` for the
 * gap column against `--text-2` for the day.
 *
 * It also RESERVES its line, which the mock does not. A gap's head appears and vanishes with the gap
 * itself — "DZIEŃ 2" at one day, nothing at zero — and an empty span is zero pixels tall, so the
 * tile under it climbed by a whole line under the very pointer that pressed −. That is a box
 * changing size as its data changes, which `CLAUDE.md`'s loading policy outranks a static mock on:
 * the mock has one state per column and cannot draw the transition between two. Only on a GAP
 * column, because a training's head is present in every state and the tail tiles' heads are absent
 * in every state; neither of those ever moves. */
.column[data-kind='rest'] .cap {
  min-height: 1lh;
  color: var(--text-3);
}

/* "~64 min", or nothing at all — never "~0 min". */
.sub {
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  color: var(--text-3);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* ── The "+ Dzień treningowy" tile ────────────────────────────────────────────────────────────
 * The mock's dashed 120px tail: `flex:1; column; centred; gap:8px; border:1px dashed var(--border-2);
 * border-radius:14px`, holding a 26×26 radius-9 `+` chip on `--bg-chip` over a two-line label. */
.add {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2);
  align-items: center;
  justify-content: center;
  padding: var(--space-0);
  font-family: var(--font-body);
  cursor: pointer;
  background: transparent;
  border: var(--border-hairline) dashed var(--border-2);
  border-radius: var(--radius-md);
}

.add:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--outline-inset);
}

.addGlyph {
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  font-size: var(--fs-session);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  background: var(--bg-chip);
  border-radius: var(--radius-mark);
}

/* The two tail tiles' drawn label, both of which the mock breaks across two lines with a literal
 * `<br>`. A locale string may not carry markup, so the break is a WIDTH the longer word cannot share
 * a line inside — see `--w-cycle-tile-label`. */
.tileLabel {
  max-width: var(--w-cycle-tile-label);
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  line-height: var(--lh-normal);
  color: var(--text-2);
  text-align: center;
}

.loop .tileLabel {
  color: var(--text);
}

/* Dimmed, focusable and carrying its reason — never the `disabled` attribute, which would drop the
 * control out of the tab order and take the explanation with it (view ADR §12).
 *
 * The DIMMING is on the content, not on the edge. It was a border-colour step alone, which is a
 * 1.31:1 edge becoming a 1.16:1 edge — a change nobody can be expected to see, on a tile whose glyph
 * and label were unchanged. `--text-3` is acceptable on these two and only these two: both are
 * inactive user-interface components, which SC 1.4.3 exempts, and neither is the sentence a trainer
 * has to read — the reason below them is drawn in full `--text-2`. */
.add[aria-disabled='true'] {
  cursor: not-allowed;
}

.add[aria-disabled='true'] .addGlyph,
.add[aria-disabled='true'] .tileLabel {
  color: var(--text-3);
}

/* Declared AFTER the refusal so the refusal wins on equal specificity — a hover over a tile at the
 * day cap must not brighten a control that cannot act. */
.add:hover:not([aria-disabled='true']) .addGlyph,
.add:hover:not([aria-disabled='true']) .tileLabel {
  color: var(--text);
}

/* Why a training cannot be added. Drawn, and DESCRIBED rather than contained — inside the button it
 * would be concatenated into its accessible name. */
.addReason {
  margin: var(--space-0);
  padding: var(--space-0) var(--space-2px);
  font-size: var(--fs-tag);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-hint-dense);
  color: var(--text-2);
  text-align: center;
}

/* ── The loop tile ────────────────────────────────────────────────────────────────────────────
 * The mock's static panel closing the run: `--bg-inset` under a SOLID `--border` hairline at radius
 * 14, centred, gap 7. Not a control — no hover, no cursor, no focus ring, because there is nothing
 * to press. */
.loop {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-7px);
  align-items: center;
  justify-content: center;
  padding: var(--space-0) var(--space-2px);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
}

/* The mock's ↻ at 20px in its own `--good`, whose LIGHT value (#0c8a52, the green meant to be read
 * rather than the #25d07f mark) this app already carries as `--repeat-pill-ink` — the pattern row's
 * pill needed the same byte, and one value under two names is the duplication the token file's own
 * header warns about. The glyph is `aria-hidden`; the tile's sentence is the accessible channel. */
.loopGlyph {
  font-size: var(--fs-tape-glyph);
  line-height: var(--lh-none);
  color: var(--repeat-pill-ink);
}

.loopHint {
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-semibold);
  color: var(--text-3);
  text-align: center;
}

/* A phase that programmes nothing — a COLUMN of the row, standing where the trainings would, with
 * the two tail tiles still beside it. It takes a training column's own flex triple so the add tile
 * lands exactly where it lands on a phase that has days, and so the row does not reflow when the
 * first one is created. */
.emptyColumn {
  display: flex;
  flex: 1 1 var(--w-cycle-day);
  min-width: var(--w-cycle-day);
  max-width: var(--w-day-max);
}

/* `EmptyState` centres its own contents; this only tells it how much of the column to occupy. */
.empty {
  flex: 1;
}
